import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query'
import { apiClient } from '@/lib/api-client'
import {
  AssetWithClassification,
  UpdateClassificationRequest,
  UpdateClassificationResponse,
  AssetFilterType,
  AssetClassificationStats,
  isNewAsset,
} from '@/types/assets'
import { AssetClass } from '@/types/api'
import { ApiError } from '@/types/api'

/**
 * Query key factory for asset-related queries
 * Organized by feature for easy invalidation
 */
export const assetKeys = {
  all: ['assets'] as const,
  lists: () => [...assetKeys.all, 'list'] as const,
  list: (filter: AssetFilterType) => [...assetKeys.lists(), filter] as const,
  details: () => [...assetKeys.all, 'detail'] as const,
  detail: (id: string) => [...assetKeys.details(), id] as const,
  stats: () => [...assetKeys.all, 'stats'] as const,
}

/**
 * Hook to fetch all assets with their classifications
 *
 * Features:
 * - 5 minute staleTime (classifications don't change often)
 * - Automatic retry on failure
 * - Type-safe AssetWithClassification return type
 *
 * @returns Query result with AssetWithClassification array
 */
export function useAssets(): UseQueryResult<AssetWithClassification[], ApiError> {
  return useQuery<AssetWithClassification[], ApiError>({
    queryKey: assetKeys.lists(),
    queryFn: async () => {
      // Per STATE.md Decision 40: Use TanStack Query for data fetching
      const response = await apiClient.getAssets()

      // Transform API response to include classification metadata
      // Backend returns Asset[], we enhance with classification info
      return response.map((asset) => ({
        ...asset,
        // Per STATE.md Decision 10: Override detection via comparison to rules-based default
        // Backend should provide rulesBasedClass - if not, assume current is default
        rulesBasedClass: (asset as AssetWithClassification).rulesBasedClass || asset.assetClass,
        isOverride: (asset as AssetWithClassification).isOverride || false,
        metadata: (asset as AssetWithClassification).metadata || {},
      }))
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - per plan requirements
    gcTime: 1000 * 60 * 10, // 10 minutes garbage collection
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

/**
 * Hook to update an asset's classification
 *
 * Features:
 * - Optimistic updates: immediately show new classification
 * - Rollback on error with error toast notification
 * - Invalidates asset queries on success
 * - Returns previous value for potential undo
 *
 * @returns Mutation result with update handlers
 */
export function useUpdateAssetClassification(): UseMutationResult<
  UpdateClassificationResponse,
  ApiError,
  { assetId: string; classification: AssetClass; isOverride: boolean }
> {
  const queryClient = useQueryClient()

  return useMutation<
    UpdateClassificationResponse,
    ApiError,
    { assetId: string; classification: AssetClass; isOverride: boolean }
  >({
    mutationFn: async ({ assetId, classification, isOverride }) => {
      // Per plan: PATCH /api/assets/:id/classification
      const requestBody: UpdateClassificationRequest = {
        assetClass: classification,
        isOverride,
      }

      const response = await fetch(`/api/assets/${assetId}/classification`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: 'Failed to update classification',
        }))
        throw new ApiError(
          errorData.message || `HTTP ${response.status}`,
          response.status,
          errorData.code,
          errorData.details
        )
      }

      const data = await response.json()
      return data as UpdateClassificationResponse
    },

    // Optimistic update: immediately update cache
    onMutate: async ({ assetId, classification, isOverride }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: assetKeys.lists() })
      await queryClient.cancelQueries({ queryKey: assetKeys.detail(assetId) })

      // Snapshot previous value for rollback
      const previousAssets = queryClient.getQueryData<AssetWithClassification[]>(
        assetKeys.lists()
      )
      const previousAsset = previousAssets?.find((a) => a.id === assetId)

      // Optimistically update the cache
      if (previousAssets) {
        queryClient.setQueryData<AssetWithClassification[]>(
          assetKeys.lists(),
          previousAssets.map((asset) =>
            asset.id === assetId
              ? {
                  ...asset,
                  assetClass: classification,
                  isOverride,
                }
              : asset
          )
        )
      }

      // Return context for rollback
      return { previousAssets, previousAsset }
    },

    // Rollback on error
    onError: (error, _variables, context) => {
      const ctx = context as { previousAssets?: AssetWithClassification[] } | undefined
      if (ctx?.previousAssets) {
        queryClient.setQueryData(assetKeys.lists(), ctx.previousAssets)
      }

      // Error toast will be handled by the UI component
      console.error('Classification update failed:', error)
    },

    // Invalidate and refetch on success
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() })
    },

    // Always refetch after error or success to ensure consistency
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() })
    },
  })
}

/**
 * Hook to reset an asset classification to rules-based default
 *
 * This removes the user override and reverts to the system-calculated classification
 */
export function useResetClassification(): UseMutationResult<
  UpdateClassificationResponse,
  ApiError,
  { assetId: string }
> {
  const queryClient = useQueryClient()

  return useMutation<UpdateClassificationResponse, ApiError, { assetId: string }>({
    mutationFn: async ({ assetId }) => {
      const response = await fetch(`/api/assets/${assetId}/classification/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: 'Failed to reset classification',
        }))
        throw new ApiError(
          errorData.message || `HTTP ${response.status}`,
          response.status,
          errorData.code,
          errorData.details
        )
      }

      const data = await response.json()
      return data as UpdateClassificationResponse
    },

    onMutate: async ({ assetId }) => {
      await queryClient.cancelQueries({ queryKey: assetKeys.lists() })

      const previousAssets = queryClient.getQueryData<AssetWithClassification[]>(
        assetKeys.lists()
      )
      const previousAsset = previousAssets?.find((a) => a.id === assetId)

      // Optimistically revert to rules-based default
      if (previousAssets && previousAsset) {
        queryClient.setQueryData<AssetWithClassification[]>(
          assetKeys.lists(),
          previousAssets.map((asset) =>
            asset.id === assetId
              ? {
                  ...asset,
                  assetClass: asset.rulesBasedClass,
                  isOverride: false,
                }
              : asset
          )
        )
      }

      return { previousAssets, previousAsset }
    },

    onError: (error, _variables, context) => {
      const ctx = context as { previousAssets?: AssetWithClassification[] } | undefined
      if (ctx?.previousAssets) {
        queryClient.setQueryData(assetKeys.lists(), ctx.previousAssets)
      }
      console.error('Classification reset failed:', error)
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() })
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() })
    },
  })
}

/**
 * Hook to get filtered assets based on filter type
 *
 * @param filter - Filter type ('all', 'crypto', 'tradfi', 'overrides', 'new')
 * @returns Filtered array of assets
 */
export function useFilteredAssets(filter: AssetFilterType): AssetWithClassification[] {
  const { data: assets = [] } = useAssets()

  return assets.filter((asset) => {
    switch (filter) {
      case 'crypto':
        return asset.assetClass === 'CRYPTO'
      case 'tradfi':
        return asset.assetClass === 'TRADFI'
      case 'overrides':
        return asset.isOverride
      case 'new':
        return isNewAsset(asset.metadata?.listingDate || asset.listingDate)
      case 'all':
      default:
        return true
    }
  })
}

/**
 * Hook to calculate asset classification statistics
 *
 * @returns Statistics object for dashboard display
 */
export function useAssetStats(): AssetClassificationStats {
  const { data: assets = [] } = useAssets()

  const cryptoCount = assets.filter((a) => a.assetClass === 'CRYPTO').length
  const tradfiCount = assets.filter((a) => a.assetClass === 'TRADFI').length
  const overrideCount = assets.filter((a) => a.isOverride).length
  const newCount = assets.filter((a) =>
    isNewAsset(a.metadata?.listingDate || a.listingDate)
  ).length
  const assetsWithPositions = assets.filter((a) => (a.positionCount || 0) > 0).length

  return {
    total: assets.length,
    cryptoCount,
    tradfiCount,
    overrideCount,
    newCount,
    assetsWithPositions,
  }
}

/**
 * Hook to search assets by ticker or name
 *
 * @param searchTerm - Search query string
 * @returns Filtered assets matching search term
 */
export function useAssetSearch(searchTerm: string): AssetWithClassification[] {
  const { data: assets = [] } = useAssets()

  if (!searchTerm.trim()) return assets

  const term = searchTerm.toLowerCase().trim()

  return assets.filter(
    (asset) =>
      asset.symbol.toLowerCase().includes(term) ||
      asset.name.toLowerCase().includes(term)
  )
}

/**
 * Hook to get a single asset by ID
 *
 * @param assetId - Asset ID to look up
 * @returns AssetWithClassification or undefined
 */
export function useAssetById(assetId: string | null): AssetWithClassification | undefined {
  const { data: assets = [] } = useAssets()

  if (!assetId) return undefined

  return assets.find((a) => a.id === assetId)
}
